import React, { useEffect, useState } from 'react';

import api from '../../api/axios';
import TrendChart from '../../components/invoice/TrendChart';
import invoiceService from '../../services/invoiceService';

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-xl p-5 ${color}`}>
    <p className="text-sm font-medium opacity-70">{label}</p>
    <p className="text-3xl font-bold mt-1">{value}</p>
  </div>
);

const BarRow = ({ label, value, max, color }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="text-sm text-gray-600 w-28 flex-shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
      <div className={`h-5 rounded-full transition-all duration-500 ${color}`}
        style={{ width: max > 0 ? `${(value / max) * 100}%` : '0%' }} />
    </div>
    <span className="text-sm font-semibold text-gray-700 w-8 text-right">{value}</span>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState(false);
  const [kamAllocation, setKamAllocation] = useState([]);
  const [kamLoading, setKamLoading] = useState(true);

  useEffect(() => {
    api.get('/api/admin/dashboard/stats')
      .then(res => setStats(res.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    invoiceService.getTrend(6)
      .then(res => setTrendData(res.data))
      .catch(() => setTrendError(true))
      .finally(() => setTrendLoading(false));
  }, []);

  useEffect(() => {
    api.get('/api/admin/kams/allocation')
      .then(res => setKamAllocation(res.data || []))
      .finally(() => setKamLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-gray-400">Loading...</div>
    </div>
  );
  if (!stats) return null;

  const maxStatus = Math.max(stats.openTickets, stats.inProgressTickets, stats.resolvedTickets, 1);
  const maxPriority = Math.max(stats.highPriorityTickets, stats.mediumPriorityTickets, stats.lowPriorityTickets, 1);

  const formatAvgTime = (hours) => {
    if (!hours || hours === 0) return 'N/A';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm">System-wide ticket overview</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard label="Total Tickets" value={stats.totalTickets} color="bg-blue-50 text-blue-700" />
          <StatCard label="Open" value={stats.openTickets} color="bg-amber-50 text-amber-700" />
          <StatCard label="In Progress" value={stats.inProgressTickets} color="bg-indigo-50 text-indigo-700" />
          <StatCard label="Resolved" value={stats.resolvedTickets} color="bg-green-50 text-green-700" />
          <div className="rounded-xl p-5 bg-purple-50 text-purple-700">
            <p className="text-sm font-medium opacity-70">Avg Resolution Time</p>
            <p className="text-3xl font-bold mt-1">{formatAvgTime(stats.avgResolutionHours)}</p>
            <p className="text-xs opacity-60 mt-1">across resolved tickets</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-700 mb-5">Status Breakdown</h2>
            <BarRow label="Open" value={stats.openTickets} max={maxStatus} color="bg-amber-400" />
            <BarRow label="In Progress" value={stats.inProgressTickets} max={maxStatus} color="bg-blue-500" />
            <BarRow label="Resolved" value={stats.resolvedTickets} max={maxStatus} color="bg-green-500" />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-700 mb-5">Priority Breakdown</h2>
            <BarRow label="High" value={stats.highPriorityTickets} max={maxPriority} color="bg-red-500" />
            <BarRow label="Medium" value={stats.mediumPriorityTickets} max={maxPriority} color="bg-amber-400" />
            <BarRow label="Low" value={stats.lowPriorityTickets} max={maxPriority} color="bg-green-500" />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-700 mb-5">KAM Enterprise Allocation</h2>
            {kamLoading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="h-3 bg-gray-200 rounded animate-pulse flex-1" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-8" />
                  </div>
                ))}
              </div>
            ) : kamAllocation.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No KAMs found</p>
            ) : (
              <div className="space-y-3">
                {kamAllocation.map((kam, i) => {
                  const maxCount = kamAllocation[0]?.enterpriseCount || 1;
                  const pct = maxCount > 0 ? (kam.enterpriseCount / maxCount) * 100 : 0;
                  return (
                    <div key={kam.kamId} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 w-28 truncate flex-shrink-0" title={kam.kamName}>
                        {kam.kamName}
                      </span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-4 rounded-full transition-all duration-500 bg-primary"
                          style={{ width: `${pct}%`, minWidth: kam.enterpriseCount > 0 ? '4px' : '0' }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-700 w-6 text-right flex-shrink-0">
                        {kam.enterpriseCount}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-700 mb-4">Billing Revenue — Month-on-Month Trend</h2>
          {trendError ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-400">Failed to load trend data</p>
            </div>
          ) : (
            <TrendChart data={trendData} loading={trendLoading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
